import React, { useState, useEffect, useRef } from 'react';
import { getBFUploadStatus, getBFUploadHistory } from '../services/api';

function fmt(n) { return (n ?? 0).toLocaleString('en-IN'); }
function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}
function fmtBizDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

function StatusDot({ color }) {
  return <span style={{ display: 'inline-block', width: 7, height: 7, borderRadius: '50%', background: color, marginRight: 6, verticalAlign: 'middle' }} />;
}

function ExchCard({ label, exchKey, statusData }) {
  const row    = statusData.find(r => r.exchange === exchKey);
  const loaded = !!row;
  return (
    <div style={{ background: '#fff', border: `1px solid ${loaded ? '#bbf7d0' : '#e2e8f0'}`, borderRadius: 8, padding: '14px 16px', borderLeft: `3px solid ${loaded ? '#16a34a' : '#e2e8f0'}` }}>
      <div style={{ fontSize: 10, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 13, fontWeight: 600, color: loaded ? '#15803d' : '#94a3b8', marginBottom: 3 }}>
        <StatusDot color={loaded ? '#22c55e' : '#d1d5db'} />
        {loaded ? 'Loaded' : 'Not uploaded yet'}
      </div>
      {loaded
        ? <div style={{ fontSize: 11, color: '#64748b' }}>{fmt(row.client_count)} clients · {fmtDate(row.uploaded_at)}</div>
        : <div style={{ fontSize: 11, color: '#9ca3af' }}>No file for today</div>}
    </div>
  );
}

export default function BFUpload() {
  const [statusData,   setStatusData]   = useState([]);
  const [history,      setHistory]      = useState([]);
  const [loadingData,  setLoadingData]  = useState(true);
  const [selectedFile, setSelectedFile] = useState(null);
  const [dragover,     setDragover]     = useState(false);
  const [uploading,    setUploading]    = useState(false);
  const [progress,     setProgress]     = useState(0);
  const [result,       setResult]       = useState(null);
  const fileInputRef = useRef();
  const xhrRef       = useRef(null);

  useEffect(() => { loadData(); }, []);

  async function loadData() {
    setLoadingData(true);
    try {
      const [statusRes, histRes] = await Promise.all([getBFUploadStatus(), getBFUploadHistory()]);
      setStatusData(statusRes.data.status  || []);
      setHistory   (histRes.data.history   || []);
    } catch (err) { console.error('BF load error:', err); }
    setLoadingData(false);
  }

  function handleFileSelect(e) { const f = e.target.files[0]; if (f) setFile(f); }
  function handleDrop(e) { e.preventDefault(); setDragover(false); const f = e.dataTransfer.files[0]; if (f) setFile(f); }
  function setFile(file) {
    if (!file.name.toLowerCase().endsWith('.csv')) { alert('Only .csv files are accepted.'); return; }
    setSelectedFile(file); setResult(null);
  }

  async function doUpload() {
    if (!selectedFile) return;
    setUploading(true); setProgress(5); setResult(null);

    const formData = new FormData();
    formData.append('bf_file', selectedFile);
    const token = localStorage.getItem('nb_admin_token');

    // ── Use XMLHttpRequest for reliable progress + timeout handling ───────────
    // fetch() has no progress events and Azure proxy cuts it silently.
    // XHR lets us track real upload progress and handle long-running requests.
    return new Promise((resolve) => {
      const xhr = new XMLHttpRequest();
      xhrRef.current = xhr;

      // Track upload progress (file transfer to server)
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) {
          const pct = Math.round((e.loaded / e.total) * 60); // 0-60% = upload phase
          setProgress(Math.max(pct, 5));
        }
      };

      // Track processing progress (server is working)
      let processingTick = 0;
      let processingInterval = null;

      xhr.upload.onload = () => {
        // File transferred — now server is processing (bulk insert)
        setProgress(65);
        processingInterval = setInterval(() => {
          processingTick++;
          // Increment slowly up to 90% — bulk insert takes 2-5 seconds
          setProgress(p => p < 90 ? p + 2 : p);
        }, 500);
      };

      xhr.onload = () => {
        clearInterval(processingInterval);
        setProgress(100);

        try {
          const data = JSON.parse(xhr.responseText);
          if (xhr.status >= 200 && xhr.status < 300 && data.success) {
            setResult({ success: true, message: data.message, data });
            setSelectedFile(null);
            if (fileInputRef.current) fileInputRef.current.value = '';
            loadData();
          } else {
            setResult({ success: false, message: data.error || 'Upload failed. Please try again.' });
          }
        } catch {
          // Response body could not be parsed — check history as fallback
          checkHistoryFallback(token);
        }

        setUploading(false);
        setTimeout(() => setProgress(0), 1500);
        resolve();
      };

      xhr.onerror = () => {
        clearInterval(processingInterval);
        // Network error — Azure proxy may have cut the connection mid-response
        // even though the insert completed. Check upload history to confirm.
        checkHistoryFallback(token);
        setUploading(false);
        setTimeout(() => setProgress(0), 1500);
        resolve();
      };

      xhr.ontimeout = () => {
        clearInterval(processingInterval);
        checkHistoryFallback(token);
        setUploading(false);
        setTimeout(() => setProgress(0), 1500);
        resolve();
      };

      // 5 minute timeout — bulk insert should complete in 2-5 seconds
      // but we give generous time for large files
      xhr.timeout = 300000;

      xhr.open('POST', '/api/bf/upload');
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      xhr.send(formData);
    });
  }

  // ── Fallback: check if upload actually succeeded despite network error ────
  async function checkHistoryFallback(token) {
    try {
      const histRes  = await fetch('/api/bf/history', {
        headers: { Authorization: `Bearer ${token}` }
      });
      const histData = await histRes.json();
      const latest   = histData.history?.[0];
      const fiveMinAgo = Date.now() - 5 * 60 * 1000;

      if (latest && new Date(latest.created_at).getTime() > fiveMinAgo) {
        // Upload completed on server side — just the response got cut
        setResult({
          success: true,
          message: `${latest.filename} processed — ${latest.inserted} positions loaded, ${latest.skipped} skipped`,
          data: { total_rows: latest.total_rows, inserted: latest.inserted, skipped: latest.skipped }
        });
        setSelectedFile(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
        loadData();
      } else {
        setResult({ success: false, message: 'Upload may have failed. Check Upload History below to confirm.' });
      }
    } catch {
      setResult({ success: false, message: 'Network error. Check Upload History below to confirm if upload succeeded.' });
    }
    setProgress(100);
    setTimeout(() => setProgress(0), 1500);
  }

  const progressLabel = () => {
    if (progress <= 0)   return '';
    if (progress < 20)   return `Uploading file... ${progress}%`;
    if (progress < 65)   return `Transferring to server... ${progress}%`;
    if (progress < 85)   return `Processing positions... ${progress}%`;
    if (progress < 100)  return `Finalising... ${progress}%`;
    return 'Complete!';
  };

  return (
    <div style={{ fontFamily: "'Segoe UI', Arial, sans-serif" }}>

      {/* Status Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 20 }}>
        <ExchCard label="NSE FO Status" exchKey="NSE" statusData={statusData} />
        <ExchCard label="MCX Status"    exchKey="MCX" statusData={statusData} />
        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: '14px 16px', borderLeft: '3px solid #2563eb' }}>
          <div style={{ fontSize: 10, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>Total Positions Loaded</div>
          <div style={{ fontSize: 22, fontWeight: 700, color: '#1d4ed8', marginBottom: 3 }}>
            {loadingData ? '—' : fmt(statusData.reduce((s, r) => s + (r.position_count || 0), 0))}
          </div>
          <div style={{ fontSize: 11, color: '#64748b' }}>across {statusData.length} exchange{statusData.length !== 1 ? 's' : ''} today</div>
        </div>
      </div>

      {/* Upload Card */}
      <div style={{ background: '#fff', border: '0.5px solid #e2e8f0', borderRadius: 10, overflow: 'hidden', marginBottom: 20 }}>
        <div style={{ padding: '14px 20px', borderBottom: '0.5px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>Upload B/F Position File</div>
            <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>Upload daily NSE FO or MCX position CSV file from SYMPHONY</div>
          </div>
          <button onClick={loadData} style={{ background: 'transparent', border: '0.5px solid #e2e8f0', color: '#64748b', padding: '5px 12px', borderRadius: 6, fontSize: 11, cursor: 'pointer' }}>↻ Refresh</button>
        </div>

        <div style={{ padding: '20px' }}>

          {/* Filename format info */}
          <div style={{ background: '#fffbeb', border: '0.5px solid #fcd34d', borderRadius: 8, padding: '12px 16px', marginBottom: 18 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: '#92400e', marginBottom: 6 }}>Expected filename formats</div>
            <div style={{ fontSize: 11, color: '#92400e', lineHeight: 1.8 }}>
              <span style={{ fontWeight: 600 }}>NSE/BSE FO:</span> SYMPHONY_NFO_CUSTOM_POSITION_UPLOAD_FILE_<span style={{ background: '#fde68a', padding: '0 3px', borderRadius: 2 }}>DD-MM-YY</span>_HH_MM_SS_AM/PM.csv<br />
              <span style={{ fontWeight: 600 }}>MCX:</span> SYMPHONY_MCX_CUSTOM_POSITION_UPLOAD_FILE_<span style={{ background: '#fde68a', padding: '0 3px', borderRadius: 2 }}>DD-MM-YY</span>_HH_MM_SS_AM/PM.csv<br />
              <span style={{ color: '#b45309' }}>Re-uploading same exchange + date will replace existing records.</span>
            </div>
          </div>

          {/* Drop zone */}
          <div
            onDragOver={e => { e.preventDefault(); setDragover(true); }}
            onDragLeave={() => setDragover(false)}
            onDrop={handleDrop}
            onClick={() => !uploading && fileInputRef.current?.click()}
            style={{ border: `2px dashed ${dragover ? '#2563eb' : selectedFile ? '#16a34a' : '#e2e8f0'}`, borderRadius: 10, background: dragover ? '#eff6ff' : selectedFile ? '#f0fdf4' : '#fafafa', padding: '32px 24px', textAlign: 'center', cursor: uploading ? 'not-allowed' : 'pointer', transition: 'all 0.15s', marginBottom: 14 }}>
            <input ref={fileInputRef} type="file" accept=".csv" onChange={handleFileSelect} style={{ display: 'none' }} />
            <div style={{ fontSize: 28, marginBottom: 10 }}>{selectedFile ? '📄' : '📂'}</div>
            {selectedFile ? (
              <>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#15803d', marginBottom: 3 }}>{selectedFile.name}</div>
                <div style={{ fontSize: 11, color: '#64748b' }}>{(selectedFile.size / 1024).toFixed(1)} KB · Click to change file</div>
              </>
            ) : (
              <>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 3 }}>Drop CSV file here or click to browse</div>
                <div style={{ fontSize: 11, color: '#9ca3af' }}>Only .csv files · Max 100 MB</div>
              </>
            )}
          </div>

          {/* Progress */}
          {uploading && (
            <div style={{ marginBottom: 14 }}>
              <div style={{ background: '#f3f4f6', borderRadius: 99, height: 6, overflow: 'hidden', marginBottom: 5 }}>
                <div style={{ background: progress < 65 ? '#2563eb' : '#16a34a', height: '100%', width: `${progress}%`, transition: 'width 0.4s ease', borderRadius: 99 }} />
              </div>
              <div style={{ fontSize: 11, color: '#64748b', textAlign: 'center' }}>{progressLabel()}</div>
            </div>
          )}

          {/* Upload button */}
          <button
            onClick={doUpload}
            disabled={!selectedFile || uploading}
            style={{ width: '100%', height: 42, background: !selectedFile || uploading ? '#93c5fd' : '#2563eb', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: !selectedFile || uploading ? 'not-allowed' : 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
            {uploading ? <>⏳ Processing — please wait...</> : <>⬆ Upload &amp; Process File</>}
          </button>

          {/* Result */}
          {result && (
            <div style={{ marginTop: 14, borderRadius: 8, padding: '14px 16px', background: result.success ? '#f0fdf4' : '#fef2f2', border: `0.5px solid ${result.success ? '#bbf7d0' : '#fecaca'}` }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: result.success ? '#15803d' : '#dc2626', marginBottom: result.success && result.data ? 10 : 0 }}>
                {result.success ? '✓ ' : '✗ '}{result.message}
              </div>
              {result.success && result.data && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 8 }}>
                  {[
                    { label: 'Total Rows', value: fmt(result.data.total_rows || (result.data.inserted + result.data.skipped)), color: '#374151' },
                    { label: 'Inserted',   value: fmt(result.data.inserted),   color: '#15803d' },
                    { label: 'Skipped',    value: fmt(result.data.skipped),    color: '#d97706' },
                  ].map(s => (
                    <div key={s.label} style={{ background: '#fff', borderRadius: 6, padding: '8px 12px', textAlign: 'center' }}>
                      <div style={{ fontSize: 18, fontWeight: 700, color: s.color }}>{s.value}</div>
                      <div style={{ fontSize: 10, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 0.4, marginTop: 2 }}>{s.label}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Upload History */}
      <div style={{ background: '#fff', border: '0.5px solid #e2e8f0', borderRadius: 10, overflow: 'hidden' }}>
        <div style={{ padding: '14px 20px', borderBottom: '0.5px solid #e2e8f0' }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>Upload History</div>
          <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>Last 20 file uploads</div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '3fr 80px 100px 80px 80px 120px 140px', padding: '9px 20px', background: '#f8fafc', borderBottom: '0.5px solid #e2e8f0' }}>
          {['Filename', 'Exchange', 'Biz Date', 'Inserted', 'Skipped', 'Uploaded By', 'Time'].map(h => (
            <div key={h} style={{ fontSize: 10, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>{h}</div>
          ))}
        </div>
        {loadingData ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#94a3b8', fontSize: 13 }}>Loading history...</div>
        ) : history.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#94a3b8', fontSize: 13 }}>No uploads yet.</div>
        ) : history.map((row, i) => (
          <div key={row.log_id || i} style={{ display: 'grid', gridTemplateColumns: '3fr 80px 100px 80px 80px 120px 140px', padding: '12px 20px', alignItems: 'center', borderBottom: i < history.length - 1 ? '0.5px solid #f1f5f9' : 'none', background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
            <div style={{ fontSize: 11.5, color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', paddingRight: 12 }} title={row.filename}>{row.filename}</div>
            <div>
              <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 4, background: row.exchange === 'NSE' ? '#eff6ff' : row.exchange === 'MCX' ? '#fff7ed' : '#f5f3ff', color: row.exchange === 'NSE' ? '#1d4ed8' : row.exchange === 'MCX' ? '#c2410c' : '#7c3aed' }}>
                {row.exchange}
              </span>
            </div>
            <div style={{ fontSize: 11.5, color: '#374151' }}>{fmtBizDate(row.biz_date)}</div>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#15803d' }}>{fmt(row.inserted)}</div>
            <div style={{ fontSize: 12, fontWeight: 600, color: row.skipped > 0 ? '#d97706' : '#9ca3af' }}>{fmt(row.skipped)}</div>
            <div style={{ fontSize: 11.5, color: '#374151' }}>{row.uploaded_by || '—'}</div>
            <div style={{ fontSize: 11, color: '#9ca3af' }}>{fmtDate(row.created_at)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}